const MB_UNIT = 1024 * 1024;

const checkFileSize = (file, maxSize = 5) => {
  if (!file || !file.size) return false;
  return file.size / MB_UNIT < maxSize;
};

const getFileExtension = (file) => {
  if (!file) return null;
  return file.name.split('.').pop();
};

const getFileName = (url) => {
  if (!url) return '';
  let fileName = url.split('/').pop();
  if (fileName.includes('?')) {
    fileName = fileName.substring(0, fileName.indexOf('?'));
  }
  // S3 URLs carry non-latin names percent-encoded; decode so link.download
  // saves the native title. Malformed sequences (stray '%') would throw.
  try {
    return decodeURIComponent(fileName);
  } catch {
    return fileName;
  }
};

/**
 * The name an exported file is saved under.
 *
 * The backend names the record without an extension while its S3 key carries
 * one. A download whose name has no extension is named by the browser from the
 * response MIME type instead, so a subtitle served as `text/plain` lands as
 * `.txt`. Reading the extension off the url keeps the type the backend built.
 */
const getDownloadFileName = (fileName, fileUrl) => {
  const fromUrl = getFileName(fileUrl);
  if (!fileName) return fromUrl;

  // Letters and digits only: a dot inside a project title ("Ban 2.0") ends up
  // in the middle of the name, and reading it as an extension would leave the
  // real one off.
  const EXTENSION = /\.[A-Za-z0-9]{1,5}$/;
  if (EXTENSION.test(fileName)) return fileName;

  const extension = fromUrl.match(EXTENSION);
  return extension ? `${fileName}${extension[0]}` : fileName;
};

const readFileAsText = async (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => resolve(null);

    reader.readAsText(file);
  });

const readFileAsArrayBuffer = async (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => resolve(null);

    reader.readAsArrayBuffer(file);
  });

const createFileFromText = (fileName, content) =>
  new File([content], fileName, { type: 'text/plain' });

const downloadFileUsingUrl = (url) => {
  const link = document.createElement('a');
  link.href = url;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const downloadFileByUrl = async (url, filename) => {
  // Fetching blob data from file URL
  const response = await fetch(url);
  const blob = await response.blob();

  // Create a download link programmatically
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);

  // Download the file from the URL
  link.click();

  // Remove element and blobUrl after downloading
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
};

const getVideoDuration = (file) =>
  new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration * 1000);
    };
    video.src = URL.createObjectURL(file);
  });

const FILE_SIZE_UNITS = [
  { threshold: 1024 ** 3, unit: 'GB' },
  { threshold: 1024 ** 2, unit: 'MB' },
  { threshold: 1024, unit: 'KB' },
];

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const { threshold, unit } =
    FILE_SIZE_UNITS.find(({ threshold: t }) => bytes >= t) ??
    FILE_SIZE_UNITS[FILE_SIZE_UNITS.length - 1];
  return `${parseFloat((bytes / threshold).toFixed(1))} ${unit}`;
};

export {
  checkFileSize,
  getFileExtension,
  getFileName,
  getDownloadFileName,
  readFileAsText,
  readFileAsArrayBuffer,
  createFileFromText,
  downloadFileUsingUrl,
  getVideoDuration,
  downloadFileByUrl,
  formatFileSize,
};
